/**
 * Write & Evaluate Algebraic Expressions generator (6.PAFR.2.2).
 *
 * Builds an expression and a substitution value (or two), then computes the
 * result independently with the exact-rational `evaluate` routine. Because the
 * answer is derived from the same engine that renders the expression, the
 * worked value can never disagree with the prompt.
 *
 * Families:
 *   E1: ax + b, evaluate at x = k
 *   E2: ax - b, evaluate at x = k (k may be negative at higher difficulty)
 *   E3: a(x + b), evaluate at x = k
 *   E4: ax + by, evaluate at x = j, y = k (two variables)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { varTerm, constant, evaluate, type Term } from "../engine/expression";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "evaluate-expressions" as const;
const VERSION = "1.0.0";

type Family = "E1" | "E2" | "E3" | "E4";

interface Cfg {
  readonly families: readonly Family[];
  readonly coeffMax: number;
  readonly constMax: number;
  readonly inputMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["E1"], coeffMax: 5, constMax: 9, inputMax: 8, pNeg: 0 },
  standard: { families: ["E1", "E2", "E3"], coeffMax: 8, constMax: 12, inputMax: 10, pNeg: 0.25 },
  hard: { families: ["E2", "E3", "E4"], coeffMax: 10, constMax: 12, inputMax: 12, pNeg: 0.45 },
  challenge: { families: ["E3", "E4"], coeffMax: 12, constMax: 15, inputMax: 12, pNeg: 0.5 },
};

/** Wrap negatives in parentheses for a clean substitution display. */
function paren(v: Rational): string {
  return v.isNegative() ? `(${v.toLatex()})` : v.toLatex();
}
function signed(v: Rational): string {
  return v.isNegative() ? `- ${v.neg().toLatex()}` : `+ ${v.toLatex()}`;
}

export class EvaluateExpressionsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      const a = rat(rng.int(2, cfg.coeffMax));
      const b = rat(rng.int(1, cfg.constMax));
      const x = rat(rng.int(1, cfg.inputMax) * (rng.bool(cfg.pNeg) ? -1 : 1));

      let terms: Term[];
      let values: Record<string, Rational>;
      let promptText: string;
      let promptLatex: string;
      let steps: { description: string; latex: string }[];

      switch (family) {
        case "E1":
        case "E2": {
          const sub = family === "E2";
          const bb = sub ? b.neg() : b;
          terms = [varTerm(a, "x"), constant(bb)];
          values = { x };
          const answer = evaluate(terms, values);
          const exprLatex = `${a.toLatex()}x ${signed(bb)}`;
          const product = a.mul(x);
          promptText = `Evaluate ${a.toString()}x ${sub ? "- " + b.toString() : "+ " + b.toString()} when x = ${x.toString()}.`;
          promptLatex = `${exprLatex}\\text{ when } x = ${x.toLatex()}`;
          steps = [
            {
              description: `Substitute ${x.toLatex()} for x.`,
              latex: `${a.toLatex()}(${paren(x)}) ${signed(bb)}`,
            },
            {
              description: "Multiply first (order of operations).",
              latex: `${product.toLatex()} ${signed(bb)}`,
            },
            { description: "Then add or subtract.", latex: answer.toLatex() },
          ];
          return this.build(ctx, topic, family, promptText, promptLatex, answer, steps);
        }
        case "E3": {
          // a(x + b) at x = k.
          terms = [varTerm(a, "x"), constant(a.mul(b))]; // = a*x + a*b
          values = { x };
          const answer = evaluate(terms, values);
          const inner = x.add(b);
          promptText = `Evaluate ${a.toString()}(x + ${b.toString()}) when x = ${x.toString()}.`;
          promptLatex = `${a.toLatex()}(x + ${b.toLatex()})\\text{ when } x = ${x.toLatex()}`;
          steps = [
            {
              description: `Substitute ${x.toLatex()} for x.`,
              latex: `${a.toLatex()}(${paren(x)} + ${b.toLatex()})`,
            },
            {
              description: "Simplify inside the parentheses first.",
              latex: `${a.toLatex()}(${inner.toLatex()})`,
            },
            { description: "Then multiply.", latex: answer.toLatex() },
          ];
          return this.build(ctx, topic, family, promptText, promptLatex, answer, steps);
        }
        case "E4": {
          // a*x + c*y at x = j, y = k.
          const c = rat(rng.int(2, cfg.coeffMax));
          const y = rat(rng.int(1, cfg.inputMax) * (rng.bool(cfg.pNeg) ? -1 : 1));
          terms = [varTerm(a, "x"), varTerm(c, "y")];
          values = { x, y };
          const answer = evaluate(terms, values);
          const px = a.mul(x);
          const py = c.mul(y);
          promptText = `Evaluate ${a.toString()}x + ${c.toString()}y when x = ${x.toString()} and y = ${y.toString()}.`;
          promptLatex = `${a.toLatex()}x + ${c.toLatex()}y\\text{ when } x = ${x.toLatex()},\\; y = ${y.toLatex()}`;
          steps = [
            {
              description: "Substitute both values.",
              latex: `${a.toLatex()}(${paren(x)}) + ${c.toLatex()}(${paren(y)})`,
            },
            {
              description: "Multiply each term.",
              latex: `${px.toLatex()} ${signed(py)}`,
            },
            { description: "Then add.", latex: answer.toLatex() },
          ];
          return this.build(ctx, topic, family, promptText, promptLatex, answer, steps);
        }
      }
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  private build(
    ctx: GenerateContext,
    topic: ReturnType<typeof getTopic>,
    family: Family,
    promptText: string,
    promptLatex: string,
    answer: Rational,
    steps: { description: string; latex: string }[],
  ): Question {
    return {
      id: `${TOPIC}-${ctx.seed}-${family}`,
      generator: TOPIC,
      generatorVersion: VERSION,
      seed: ctx.seed,
      unit: topic.unit,
      topic: TOPIC,
      family,
      difficulty: ctx.difficulty,
      prompt: { text: promptText, latex: promptLatex },
      answerType: "number",
      answer: { kind: "number", value: answer },
      steps,
      hints: [
        { level: 1, text: "Substitute the given value(s) for the variable(s)." },
        { level: 2, text: "Follow order of operations: multiply before adding." },
      ],
      subskills: topic.subskills,
    };
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const evaluateExpressions = new EvaluateExpressionsGenerator();
